// Info Service - API calls for info upload and extraction

import api from './api';
import { buildQueryString } from '../utils/urlHelpers';
import type {
  UploadResponse,
  ExtractionResponse,
  InfoSubmission,
  RefinementRequest,
  ApprovalRequest,
  ApprovalResponse,
  TranscriptionResult,
  SubmissionStatus,
} from '../types/info';

export const infoService = {
  /**
   * Upload content (files, text, audio) for extraction
   */
  upload: async (
    files: File[],
    textContent: string | null,
    audioFiles: File[],
  ): Promise<UploadResponse> => {
    const formData = new FormData();

    // Add document files
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Add text content
    if (textContent && textContent.trim()) {
      formData.append('text_content', textContent.trim());
    }

    // Add audio files
    audioFiles.forEach((file) => {
      formData.append('audio_files', file);
    });

    const response = await api.post<UploadResponse>('/info/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for transcription
    });

    return response.data;
  },

  /**
   * Transcribe a single audio file (for preview)
   */
  transcribe: async (audioFile: File): Promise<TranscriptionResult> => {
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await api.post<TranscriptionResult>('/info/transcribe', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 1 minute
    });

    return response.data;
  },

  /**
   * Run LLM extraction on a submission
   */
  extract: async (submissionId: string): Promise<ExtractionResponse> => {
    const response = await api.post<ExtractionResponse>(
      `/info/${submissionId}/extract`,
      {},
      { timeout: 120000 } // 2 minutes for extraction
    );
    return response.data;
  },

  /**
   * Refine extraction with user feedback
   */
  refine: async (
    submissionId: string,
    request: RefinementRequest,
  ): Promise<ExtractionResponse> => {
    const response = await api.post<ExtractionResponse>(
      `/info/${submissionId}/refine`,
      request,
      { timeout: 120000 }
    );
    return response.data;
  },

  /**
   * Approve extracted items
   */
  approve: async (
    submissionId: string,
    request?: ApprovalRequest,
  ): Promise<ApprovalResponse> => {
    const response = await api.post<ApprovalResponse>(
      `/info/${submissionId}/approve`,
      request || {},
    );
    return response.data;
  },

  /**
   * Reject entire submission
   */
  reject: async (submissionId: string): Promise<{ status: string; message: string }> => {
    const response = await api.post<{ status: string; message: string }>(
      `/info/${submissionId}/reject`,
    );
    return response.data;
  },

  /**
   * Get a specific submission
   */
  getSubmission: async (submissionId: string): Promise<InfoSubmission> => {
    const response = await api.get<InfoSubmission>(`/info/${submissionId}`);
    return response.data;
  },

  /**
   * List user's submissions
   */
  listSubmissions: async (
    statusFilter?: SubmissionStatus,
    limit: number = 20,
    offset: number = 0,
  ): Promise<InfoSubmission[]> => {
    const queryString = buildQueryString({
      status_filter: statusFilter,
      limit,
      offset,
    });

    const response = await api.get<InfoSubmission[]>(`/info/submissions?${queryString}`);
    return response.data;
  },
};

export default infoService;
